import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import Snackbar from "../../Snackbar/Snackbar";
import useDevices from "../../../../hooks/useDevices/useDevices";
import useVideoContext from "../../../../hooks/useVideoContext/useVideoContext";

export function getSnackbarContent(
  hasAudio: boolean,
  hasVideo: boolean,
  t: TFunction,
  error?: Error
) {
  let headline = "";
  let message = "";

  switch (true) {
    // These custom errors are thrown by the useLocalTracks hook. They are thrown when the user explicitly denies
    // permission to only their camera, or only their microphone.
    case error?.message === "CameraPermissionsDenied":
      headline = t("mediaError.unableToAccess");
      message = t("mediaError.cameraDenied");
      break;
    case error?.message === "MicrophonePermissionsDenied":
      headline = t("mediaError.unableToAccess");
      message = t("mediaError.micDenied");
      break;

    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === "NotAllowedError":
      headline = t("mediaError.unableToAccess");
      message =
        error!.message === "Permission denied by system"
          ? t("mediaError.systemBlocked") // Chrome only
          : t("mediaError.audioVideoDenied");
      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === "NotFoundError":
      headline = t("mediaError.notFoundHeadline");
      message = t("mediaError.notFound");
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = t("mediaError.errorAcquiringHeadline");
      message = `${error!.name} ${error!.message}`;
      break;

    case !hasAudio && !hasVideo:
      headline = t("mediaError.noCameraOrMicHeadline");
      message = t("mediaError.noCameraOrMic");
      break;

    case !hasVideo:
      headline = t("mediaError.noCameraHeadline");
      message = t("mediaError.noCamera");
      break;

    case !hasAudio:
      headline = t("mediaError.noMicHeadline");
      message = t("mediaError.noMic");
  }

  return {
    headline,
    message
  };
}

export default function MediaErrorSnackbar({ error }: { error?: Error }) {
  const { t } = useTranslation();
  const { hasAudioInputDevices, hasVideoInputDevices } = useDevices();

  const { isAcquiringLocalTracks } = useVideoContext();

  const [isSnackbarDismissed, setIsSnackbarDismissed] = useState(false);

  const isSnackbarOpen =
    !isSnackbarDismissed &&
    !isAcquiringLocalTracks &&
    (Boolean(error) || !hasAudioInputDevices || !hasVideoInputDevices);

  const { headline, message } = getSnackbarContent(
    hasAudioInputDevices,
    hasVideoInputDevices,
    t,
    error
  );

  return (
    <Snackbar
      open={isSnackbarOpen}
      handleClose={() => setIsSnackbarDismissed(true)}
      headline={headline}
      message={message}
      variant="warning"
    />
  );
}
